import {
  WorkflowDefinitionGraphDto,
  WorkflowFieldDto,
  WorkflowGateDto,
  WorkflowGraphDto,
  WorkflowSaveFieldRequestDto,
  WorkflowSaveRequestDto,
  WorkflowSaveResultDto,
  WorkflowSaveStageRequestDto,
  WorkflowSaveTransitionRequestDto,
  WorkflowSaveVariableRequestDto,
  WorkflowStageDto,
  WorkflowTransitionDto,
} from "../../types/workflowDefinitionDto";
import {
  createSeedCanvas,
  createWorkflowApprovalConfig,
  createWorkflowCriterion,
  isFieldPersonSource,
  WORKFLOW_END_ROUTE,
  WorkflowCanvas,
  WorkflowFormField,
} from "./workflowBuilderData";
import {
  asArray,
  asNumberOrNull,
  asObject,
  asString,
  criteriaFromRules,
  fromSettingsJson,
  mergeOverServerJson,
  parseJsonObject,
  settingsShape,
  pauseUnitFromApi,
  TASK_ID_PREFIX,
  toConditionJson,
  toConfigJson,
  toResolverSource,
  fromResolverSource,
  fromAssignModel,
  approvalFromConfig,
  emailFromConfig,
  cancelFromConfig,
  toSettingsJson,
  VISIBILITY_TO_API,
} from "./workflowStageConfigJson";

/**
 * Translates between the API's flat workflow graph (Epic 80372: stages / transitions / fields /
 * options / variables, joined by ids) and the Builder's ordered `WorkflowCanvas[]`.
 *
 * **Transitions are the authoritative routes.** The server's reachability / dangling checks and
 * the runtime both walk them, and they carry real stage ids, so loading always rebuilds routes from
 * them. Linear order becomes a `default` transition to the next canvas; approval gates become
 * accept/reject/cancel transitions; conditional paths become `path` transitions plus a trailing
 * `default` to the next canvas as the catch-all. An unrouted gate or path emits no transition, so a
 * half-configured draft still saves and Validate reports it instead. (ConfigJson repeats the
 * routes only for Validate — see workflowStageConfigJson.ts.)
 *
 * Saved rows are identified by their client ids — `wf-stage-{id}`, `wf-field-{id}` — so the next
 * save updates them in place rather than soft-deleting and re-inserting them.
 */

const STAGE_PREFIX = "wf-stage-";
const FIELD_PREFIX = "wf-field-";

/** Path transitions are ordered by SortVal; this spacing also encodes the path's index in the list,
 *  so an unrouted path (no transition) does not shift the others on reload. */
const PATH_SORT_STEP = 10;
const DEFAULT_SORT = 1000;

/** Ids carried from a load to the next save for rows the UI model has no id slot for. Keyed by the
 *  same strings the save payload uses as clientIds. */
export interface WorkflowGraphKeys {
  transitionIds: Record<string, number>;
  optionIds: Record<string, number>;
  variableIds: Record<string, number>;
}

export interface WorkflowBuilderGraph {
  definition: WorkflowDefinitionGraphDto;
  canvases: WorkflowCanvas[];
  keys: WorkflowGraphKeys;
}

/** The definition-level columns a save carries. `id` 0 creates a new draft. */
export type WorkflowSaveHeader = Pick<
  WorkflowSaveRequestDto,
  | "id"
  | "name"
  | "token"
  | "description"
  | "moduleId"
  | "category"
  | "titleFormula"
  | "ownerRule"
  | "rowVersionTag"
>;

export const createEmptyGraphKeys = (): WorkflowGraphKeys => ({
  transitionIds: {},
  optionIds: {},
  variableIds: {},
});

export const stageClientId = (stageId: number) => `${STAGE_PREFIX}${stageId}`;
const fieldClientId = (fieldId: number) => `${FIELD_PREFIX}${fieldId}`;

/** The persisted id behind a client id, or 0 for a row that has never been saved. */
const entityIdOf = (clientId: string, prefix: string): number => {
  if (!clientId.startsWith(prefix)) return 0;
  const parsed = Number(clientId.slice(prefix.length));
  return Number.isInteger(parsed) && parsed > 0 ? parsed : 0;
};

const transitionKey = (fromClientId: string, gate: WorkflowGateDto, discriminator = "") =>
  `${fromClientId}|${gate}|${discriminator}`;

const optionKey = (fieldId: string, name: string) => `${fieldId}|${name}`;

// ----- field keys -----

const slugFieldKey = (label: string) => {
  const words = label.replace(/[^A-Za-z0-9 ]+/g, " ").trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "";
  const key = words
    .map((word, index) =>
      index === 0 ? word.toLowerCase() : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join("");
  return /^[0-9]/.test(key) ? `f${key}` : key;
};

/** FieldKey doubles as the field's variable key, so it must be unique across the whole workflow.
 *  A persisted key is kept as-is (renaming the label must not break conditions that reference it);
 *  a new one is derived from the label and suffixed on collision. */
const assignFieldKeys = (canvases: WorkflowCanvas[]): Map<string, string> => {
  const keys = new Map<string, string>();
  const used = new Set<string>();
  const allFields = canvases.flatMap((canvas) => (canvas.type === "form" ? canvas.fields : []));

  allFields.forEach((field) => {
    if (field.fieldKey && !used.has(field.fieldKey)) {
      keys.set(field.id, field.fieldKey);
      used.add(field.fieldKey);
    }
  });

  allFields.forEach((field, index) => {
    if (keys.has(field.id)) return;
    const base = slugFieldKey(field.label) || `field${index + 1}`;
    let candidate = base;
    let suffix = 2;
    while (used.has(candidate)) candidate = `${base}${suffix++}`;
    keys.set(field.id, candidate);
    used.add(candidate);
  });

  return keys;
};

// ----- builder → save request -----

const buildFields = (
  canvas: WorkflowCanvas,
  fieldKeys: Map<string, string>,
  keys: WorkflowGraphKeys,
): WorkflowSaveFieldRequestDto[] =>
  canvas.type !== "form"
    ? []
    : canvas.fields.map((field, index) => ({
        clientId: field.id,
        id: entityIdOf(field.id, FIELD_PREFIX),
        fieldKey: fieldKeys.get(field.id) ?? field.id,
        label: field.label,
        dataType: field.dataType,
        isRequired: field.required,
        isReadOnly: field.readOnly,
        // No editor for these yet; a loaded field passes its own back rather than clearing them.
        defaultValue: field.defaultValue ?? null,
        visibilityRule: VISIBILITY_TO_API[field.visibility],
        validationJson: field.validationJson ?? null,
        settingsJson: toSettingsJson(field),
        helpUrl: field.help || null,
        sortVal: (index + 1) * 10,
        // Sent for dropdowns only. Other types keep their options client-side so switching back
        // restores them, but they aren't part of the saved field.
        options:
          field.dataType !== "dropdown"
            ? []
            : field.options.map((name, optionIndex) => ({
                clientId: `${field.id}|opt|${optionIndex}`,
                id: keys.optionIds[optionKey(field.id, name)] ?? 0,
                name,
                // The stored Value is what the runtime matches on (the PIF's "not-yet"); the Builder
                // only edits names, so a loaded option keeps its own and a new one uses its name.
                value: field.optionValues?.[name] ?? name,
                sortVal: (optionIndex + 1) * 10,
              })),
      }));

const buildTransitions = (
  canvases: WorkflowCanvas[],
  keys: WorkflowGraphKeys,
): WorkflowSaveTransitionRequestDto[] => {
  const canvasIds = new Set(canvases.map((canvas) => canvas.id));
  const transitions: WorkflowSaveTransitionRequestDto[] = [];

  const push = (
    from: string,
    gate: WorkflowGateDto,
    to: string | null,
    extra: Partial<WorkflowSaveTransitionRequestDto> = {},
    discriminator = "",
  ) => {
    const key = transitionKey(from, gate, discriminator);
    transitions.push({
      clientId: key,
      id: keys.transitionIds[key] ?? 0,
      fromClientId: from,
      toClientId: to,
      gate,
      pathLabel: null,
      conditionJson: null,
      sortVal: DEFAULT_SORT,
      ...extra,
    });
  };

  // A route can still point at a canvas deleted after it was chosen. Sending that id would 422 the
  // whole save with UNKNOWN_TARGET, so it is dropped and Validate reports the gate as unrouted.
  // "End workflow" is a transition with no target (`to: null`), which the runtime treats as the end.
  const END = { to: null } as const;
  const routed = (routeId: string): { to: string | null } | null =>
    routeId === WORKFLOW_END_ROUTE ? END : routeId && canvasIds.has(routeId) ? { to: routeId } : null;

  canvases.forEach((canvas, index) => {
    const next = canvases[index + 1]?.id ?? null;

    switch (canvas.type) {
      case "finalize":
      case "cancel":
        return;
      case "approval": {
        const { approval } = canvas;
        const accept = routed(approval.acceptRouteId);
        const reject = routed(approval.rejectRouteId);
        const cancel = routed(approval.cancelRouteId);
        if (accept) push(canvas.id, "accept", accept.to, { sortVal: 10 });
        if (reject) push(canvas.id, "reject", reject.to, { sortVal: 20 });
        if (cancel) push(canvas.id, "cancel", cancel.to, { sortVal: 30 });
        return;
      }
      case "conditional":
        canvas.paths.forEach((path, pathIndex) => {
          const target = routed(path.routeId);
          if (!target) return;
          push(
            canvas.id,
            "path",
            target.to,
            {
              pathLabel: path.name || null,
              conditionJson: toConditionJson(path),
              sortVal: (pathIndex + 1) * PATH_SORT_STEP,
            },
            String(pathIndex),
          );
        });
        if (next && canvas.catchAllToNext !== false) push(canvas.id, "default", next);
        return;
      case "pause": {
        // An unset "Then go to" continues to the next canvas. A target deleted since is NOT
        // silently replaced by the next canvas: no transition, so Validate flags the route.
        const { routeId } = canvas.pause;
        const target = routeId ? routed(routeId) : next ? { to: next } : null;
        if (target) push(canvas.id, "default", target.to);
        return;
      }
      default:
        if (next) push(canvas.id, "default", next);
    }
  });

  return transitions;
};

/** One carried-forward variable per form field, keyed by its FieldKey — what a Conditional's
 *  criteria and a web-service URL chip refer to. */
const buildVariables = (
  canvases: WorkflowCanvas[],
  fieldKeys: Map<string, string>,
  keys: WorkflowGraphKeys,
): WorkflowSaveVariableRequestDto[] =>
  canvases.flatMap((canvas) =>
    canvas.type !== "form"
      ? []
      : canvas.fields.map((field) => {
          const varKey = fieldKeys.get(field.id) ?? field.id;
          return {
            clientId: `var|${varKey}`,
            id: keys.variableIds[varKey] ?? 0,
            varKey,
            nickname: field.label || null,
            dataType: field.dataType,
            sourceClientId: canvas.id,
            sourceFieldClientId: field.id,
          };
        }),
  );

export interface WorkflowVariableOption {
  /** What goes between the braces of a {{chip}} — the runtime looks values up by this key. */
  key: string;
  /** Where the value comes from, for the picker: a field's label or the capturing stage. */
  source: string;
}

/**
 * The variables a stage's {{chips}} can use: the form fields (by the FieldKey the save sends) and
 * web-service captures of the stages before it. Later stages haven't produced their values yet
 * when this one runs. An unknown stage id (not in the flow yet) sees every other stage.
 */
export const workflowVariablesBefore = (
  canvases: WorkflowCanvas[],
  canvasId: string,
): WorkflowVariableOption[] => {
  const fieldKeys = assignFieldKeys(canvases);
  const index = canvases.findIndex((canvas) => canvas.id === canvasId);
  const earlier = (index < 0 ? canvases : canvases.slice(0, index)).filter(
    (canvas) => canvas.id !== canvasId,
  );
  const seen = new Set<string>();
  const options: WorkflowVariableOption[] = [];
  const add = (key: string, source: string) => {
    if (!key || seen.has(key.toLowerCase())) return;
    seen.add(key.toLowerCase());
    options.push({ key, source });
  };

  earlier.forEach((canvas) => {
    if (canvas.type === "form") {
      canvas.fields.forEach((field) =>
        add(fieldKeys.get(field.id) ?? "", `${field.label || "Field"} (${canvas.name})`),
      );
    } else if (canvas.type === "webservice") {
      canvas.webService.captures.forEach((capture) =>
        add(capture.variable.trim(), `Captured by ${canvas.name}`),
      );
    }
  });
  return options;
};

/**
 * A canvas owner on the wire: OwnerRule names the source in PersonResolver's vocabulary, a named
 * owner goes in OwnerUserId, and a field source's field key rides in ConfigJson as ownerFieldKey
 * (where PersonResolver.ReadOwnerFieldKey looks for it).
 */
const ownerColumns = (canvas: WorkflowCanvas) => {
  if (!canvas.ownerSource) return { ownerRule: null, ownerUserId: null, ownerFieldKey: null };
  const userId = Number(canvas.owner);
  return {
    ownerRule: toResolverSource(canvas.ownerSource),
    ownerUserId:
      canvas.ownerSource === "specific-user" && Number.isInteger(userId) && userId > 0 ? userId : null,
    ownerFieldKey: isFieldPersonSource(canvas.ownerSource) && canvas.owner.trim() ? canvas.owner.trim() : null,
  };
};

/** The ConfigJson keys the Builder itself writes for a canvas — also a loaded stage's baseline. */
const writtenStageConfig = (canvas: WorkflowCanvas): Record<string, unknown> => {
  const written = parseJsonObject(toConfigJson(canvas));
  const { ownerFieldKey } = ownerColumns(canvas);
  return ownerFieldKey ? { ...written, ownerFieldKey } : written;
};

/** A loaded stage keeps the ConfigJson keys the author didn't change (see mergeOverServerJson). */
const stageConfigJson = (canvas: WorkflowCanvas) =>
  JSON.stringify(mergeOverServerJson(writtenStageConfig(canvas), canvas.serverConfig));

/** The single whole-graph save payload — the Builder never saves per row. */
export const builderToSaveRequest = (
  header: WorkflowSaveHeader,
  canvases: WorkflowCanvas[],
  keys: WorkflowGraphKeys,
): WorkflowSaveRequestDto => {
  const fieldKeys = assignFieldKeys(canvases);
  const stages: WorkflowSaveStageRequestDto[] = canvases.map((canvas, index) => {
    const { ownerRule, ownerUserId } = ownerColumns(canvas);
    return {
      clientId: canvas.id,
      id: entityIdOf(canvas.id, STAGE_PREFIX),
      stageType: canvas.type,
      name: canvas.name,
      summary: canvas.summary || null,
      ownerRule,
      ownerUserId,
      isStart: index === 0,
      posX: null,
      posY: null,
      sortVal: (index + 1) * 10,
      configJson: stageConfigJson(canvas),
      stageKey: canvas.stageKey || null,
      fields: buildFields(canvas, fieldKeys, keys),
    };
  });

  return {
    ...header,
    stages,
    transitions: buildTransitions(canvases, keys),
    variables: buildVariables(canvases, fieldKeys, keys),
  };
};

// ----- graph → builder -----

const bySort = <T extends { sortVal: number | null; id: number }>(a: T, b: T) =>
  (a.sortVal ?? Number.MAX_SAFE_INTEGER) - (b.sortVal ?? Number.MAX_SAFE_INTEGER) || a.id - b.id;

const fieldFromDto = (
  field: WorkflowFieldDto,
  options: string[],
  optionValues: Record<string, string>,
): WorkflowFormField => {
  const loaded: WorkflowFormField = {
    id: fieldClientId(field.id),
    fieldKey: field.fieldKey,
    label: field.label,
    dataType: field.dataType,
    required: field.isRequired,
    readOnly: field.isReadOnly,
    visibility: field.visibilityRule?.toLowerCase() === "hide" ? "hide" : "show",
    help: field.helpUrl ?? "",
    options,
    settings: fromSettingsJson(field.settingsJson),
    defaultValue: field.defaultValue,
    validationJson: field.validationJson,
    optionValues,
  };
  return {
    ...loaded,
    serverSettings: { raw: parseJsonObject(field.settingsJson), baseline: settingsShape(loaded) },
  };
};

/** The inverse of ownerColumns. An OwnerRule the resolver doesn't know loads as "not set". */
const ownerFromStage = (
  stage: WorkflowStageDto,
  config: Record<string, unknown>,
): Pick<WorkflowCanvas, "ownerSource" | "owner"> => {
  const ownerSource = fromResolverSource(stage.ownerRule) ?? "";
  if (ownerSource === "specific-user") {
    return { ownerSource, owner: stage.ownerUserId ? String(stage.ownerUserId) : "" };
  }
  if (isFieldPersonSource(ownerSource)) {
    return { ownerSource, owner: asString(config.ownerFieldKey) };
  }
  return { ownerSource, owner: "" };
};

const canvasFromStage = (
  stage: WorkflowStageDto,
  outgoing: WorkflowTransitionDto[],
  fields: WorkflowFormField[],
): WorkflowCanvas => {
  const config = parseJsonObject(stage.configJson);
  // No transition = unrouted; a transition with no target = "End workflow".
  const target = (transition: WorkflowTransitionDto | undefined) =>
    !transition ? "" : transition.toStageId ? stageClientId(transition.toStageId) : WORKFLOW_END_ROUTE;
  const byGate = (gate: WorkflowGateDto) => outgoing.find((transition) => transition.gate === gate);
  const keyValues = (value: unknown, prefix: string) =>
    asArray(value).map((raw, index) => {
      const pair = asObject(raw);
      return { id: `${prefix}-${stage.id}-${index}`, key: asString(pair.key), value: asString(pair.value) };
    });

  const canvas = createSeedCanvas({
    id: stageClientId(stage.id),
    type: stage.stageType,
    name: stage.name,
    summary: stage.summary ?? "",
    ...ownerFromStage(stage, config),
    // Only ever the server's key. An existing stage's key is permanent (the PIF's pif-1..pif-9), so
    // when Graph_S doesn't return one the canvas carries none and saves null — never a made-up key
    // that would overwrite the stored one.
    stageKey: stage.stageKey ?? "",
    fields,
  });

  switch (stage.stageType) {
    case "approval": {
      canvas.approval = createWorkflowApprovalConfig({
        ...approvalFromConfig(config, canvas.approval),
        acceptRouteId: target(byGate("accept")),
        rejectRouteId: target(byGate("reject")),
        cancelRouteId: target(byGate("cancel")),
      });
      break;
    }
    case "task":
      canvas.tasks = asArray(config.tasks).map((entry, index) => {
        const task = asObject(entry);
        return {
          id: `${TASK_ID_PREFIX}${asString(task.taskKey) || `task-${index + 1}`}`,
          title: asString(task.title),
          description: asString(task.description),
          assigneeSource: fromAssignModel(task.assignModel) ?? "specific-user",
          assignee: asString(task.assigneeRef),
        };
      });
      break;
    case "conditional": {
      const pathTransitions = outgoing.filter((transition) => transition.gate === "path").sort(bySort);
      const configPaths = asArray(config.paths);
      if (configPaths.length > 0) {
        // Saved by the Builder: the list lives in ConfigJson (unrouted paths included) and each
        // routed path's transition sits at its index's SortVal step.
        canvas.paths = configPaths.map((entry, index) => {
          const path = asObject(entry);
          const criteria = criteriaFromRules(path.criteria);
          return {
            id: `path-${stage.id}-${index}`,
            name: asString(path.name, `Path ${index + 1}`),
            logic: asString(path.logic) === "OR" ? "OR" : "AND",
            criteria: criteria.length > 0 ? criteria : [createWorkflowCriterion()],
            routeId: target(
              pathTransitions.find((transition) => transition.sortVal === (index + 1) * PATH_SORT_STEP),
            ),
          };
        });
      } else {
        // Built outside the Builder (the seeded PIF): the transitions ARE the paths, in SortVal
        // order, each with its label and ConditionJson. Reading only ConfigJson here would load no
        // paths and the next save would delete every route.
        canvas.paths = pathTransitions.map((transition, index) => {
          const condition = parseJsonObject(transition.conditionJson);
          const criteria = criteriaFromRules(condition.rules);
          return {
            id: `path-${stage.id}-${index}`,
            name: transition.pathLabel ?? `Path ${index + 1}`,
            logic: asString(condition.op) === "OR" ? "OR" : "AND",
            criteria: criteria.length > 0 ? criteria : [createWorkflowCriterion()],
            routeId: target(transition),
          };
        });
        canvas.catchAllToNext = outgoing.some((transition) => transition.gate === "default");
      }
      break;
    }
    case "webservice":
      canvas.webService = {
        method: (asString(config.method) || "GET") as WorkflowCanvas["webService"]["method"],
        url: asString(config.url),
        onFailure: asString(config.onFailure) === "cancel" ? "cancel" : "continue",
        params: keyValues(config.params, "param"),
        headers: keyValues(config.headers, "header"),
        captures: asArray(config.responseCaptures).map((raw, index) => {
          const capture = asObject(raw);
          return {
            id: `capture-${stage.id}-${index}`,
            responseField: asString(capture.path),
            variable: asString(capture.var),
          };
        }),
        body: asString(config.bodyTemplate),
      };
      break;
    case "email":
      canvas.email = emailFromConfig(config);
      break;
    case "pause":
      canvas.pause = {
        amount: asNumberOrNull(config.amount) ?? canvas.pause.amount,
        unit: pauseUnitFromApi(config.unit),
        routeId: target(byGate("default")),
      };
      break;
    case "cancel":
      canvas.cancel = cancelFromConfig(config);
      break;
    default:
      break;
  }

  return canvas;
};

export const graphToBuilder = (graph: WorkflowGraphDto): WorkflowBuilderGraph => {
  const keys = createEmptyGraphKeys();

  const optionsByField = new Map<number, string[]>();
  const optionValuesByField = new Map<number, Record<string, string>>();
  [...graph.fieldOptions].sort(bySort).forEach((option) => {
    const list = optionsByField.get(option.workflowFieldId) ?? [];
    list.push(option.name);
    optionsByField.set(option.workflowFieldId, list);
    if (option.value !== null) {
      optionValuesByField.set(option.workflowFieldId, {
        ...optionValuesByField.get(option.workflowFieldId),
        [option.name]: option.value,
      });
    }
    keys.optionIds[optionKey(fieldClientId(option.workflowFieldId), option.name)] = option.id;
  });

  graph.variables.forEach((variable) => {
    keys.variableIds[variable.varKey] = variable.id;
  });

  const canvases = [...graph.stages].sort(bySort).map((stage) => {
    const outgoing = graph.transitions.filter((transition) => transition.fromStageId === stage.id);
    // Record each transition's id under the key the next save rebuilds it with. A path transition
    // is keyed by its path index: recovered from the SortVal spacing for a Builder-saved list (paths
    // in ConfigJson), or its position in SortVal order when the transitions are the paths (the PIF).
    const pathsFromTransitions = asArray(parseJsonObject(stage.configJson).paths).length === 0;
    const pathOrder = outgoing.filter((transition) => transition.gate === "path").sort(bySort);
    outgoing.forEach((transition) => {
      const discriminator =
        transition.gate !== "path"
          ? ""
          : pathsFromTransitions
            ? String(pathOrder.indexOf(transition))
            : transition.sortVal
              ? String(transition.sortVal / PATH_SORT_STEP - 1)
              : "";
      keys.transitionIds[transitionKey(stageClientId(stage.id), transition.gate, discriminator)] =
        transition.id;
    });

    const fields = graph.fields
      .filter((field) => field.workflowStageId === stage.id)
      .sort(bySort)
      .map((field) =>
        fieldFromDto(field, optionsByField.get(field.id) ?? [], optionValuesByField.get(field.id) ?? {}),
      );

    const canvas = canvasFromStage(stage, outgoing, fields);
    // The baseline is taken from the canvas exactly as loaded, so a save can tell an edit apart
    // from a key the Builder merely rewrites differently (see mergeOverServerJson).
    canvas.serverConfig = { raw: parseJsonObject(stage.configJson), baseline: writtenStageConfig(canvas) };
    return canvas;
  });

  return { definition: graph.definition, canvases, keys };
};

// ----- re-keying -----

/** Moves canvases (and fields) onto new client ids; every route that pointed at a renamed canvas
 *  follows it. Saved field/task keys, when given, are stamped on so they stay stable. */
const renameCanvases = (
  canvases: WorkflowCanvas[],
  stageRename: Map<string, string>,
  fieldRename: Map<string, string>,
  savedFieldKeys: Map<string, string> = new Map(),
  savedTaskKeys: Map<string, string[]> = new Map(),
): WorkflowCanvas[] => {
  const renameStage = (id: string) => (id ? stageRename.get(id) ?? id : id);
  return canvases.map((canvas) => {
    const taskKeys = savedTaskKeys.get(canvas.id);
    return {
      ...canvas,
      id: renameStage(canvas.id),
      fields: canvas.fields.map((field) => ({
        ...field,
        id: fieldRename.get(field.id) ?? field.id,
        fieldKey: savedFieldKeys.get(field.id) ?? field.fieldKey,
      })),
      tasks: canvas.tasks.map((task, index) =>
        taskKeys?.[index] ? { ...task, id: `${TASK_ID_PREFIX}${taskKeys[index]}` } : task,
      ),
      approval: {
        ...canvas.approval,
        acceptRouteId: renameStage(canvas.approval.acceptRouteId),
        rejectRouteId: renameStage(canvas.approval.rejectRouteId),
        cancelRouteId: renameStage(canvas.approval.cancelRouteId),
      },
      paths: canvas.paths.map((path) => ({ ...path, routeId: renameStage(path.routeId) })),
      pause: { ...canvas.pause, routeId: renameStage(canvas.pause.routeId) },
    };
  });
};

/**
 * Detaches canvases from the rows they were loaded from, so they can be saved into a DIFFERENT
 * definition (Save as new version carrying unsaved edits): every stage and field is sent as new
 * (id 0), and the copy's own rows — absent from the payload — are soft-deleted by the save.
 * Field keys are kept, so the variables and conditions that reference them still line up.
 */
export const forgetPersistedIds = (canvases: WorkflowCanvas[]): WorkflowCanvas[] => {
  const stageRename = new Map<string, string>();
  const fieldRename = new Map<string, string>();
  canvases.forEach((canvas) => {
    const stageId = entityIdOf(canvas.id, STAGE_PREFIX);
    if (stageId) stageRename.set(canvas.id, `carried-stage-${stageId}`);
    canvas.fields.forEach((field) => {
      const fieldId = entityIdOf(field.id, FIELD_PREFIX);
      if (fieldId) fieldRename.set(field.id, `carried-field-${fieldId}`);
    });
  });
  return renameCanvases(canvases, stageRename, fieldRename);
};

// ----- after a save -----

/**
 * Re-keys the canvases onto the real ids a save just assigned, so the NEXT save updates those rows
 * instead of inserting them again; every route that pointed at a renamed canvas follows it. Also
 * returns the keys for the next save, rebuilt from the request that was sent, and the old → new
 * canvas ids so the caller can carry its selection across.
 */
export const applySaveResult = (
  canvases: WorkflowCanvas[],
  request: WorkflowSaveRequestDto,
  result: Pick<WorkflowSaveResultDto, "idMap">,
): {
  canvases: WorkflowCanvas[];
  keys: WorkflowGraphKeys;
  stageIdRenames: Record<string, string>;
} => {
  const { idMap } = result;
  const stageRename = new Map<string, string>();
  const fieldRename = new Map<string, string>();
  const savedFieldKeys = new Map<string, string>();
  const savedTaskKeys = new Map<string, string[]>();

  request.stages.forEach((stage) => {
    const stageId = idMap.stages[stage.clientId] ?? stage.id;
    if (stageId > 0) stageRename.set(stage.clientId, stageClientId(stageId));
    stage.fields.forEach((field) => {
      const fieldId = idMap.fields[field.clientId] ?? field.id;
      if (fieldId > 0) fieldRename.set(field.clientId, fieldClientId(fieldId));
      savedFieldKeys.set(field.clientId, field.fieldKey);
    });
    if (stage.stageType === "task") {
      savedTaskKeys.set(
        stage.clientId,
        asArray(parseJsonObject(stage.configJson).tasks).map((entry) => asString(asObject(entry).taskKey)),
      );
    }
  });

  const renameStage = (id: string) => (id ? stageRename.get(id) ?? id : id);
  const nextCanvases = renameCanvases(canvases, stageRename, fieldRename, savedFieldKeys, savedTaskKeys);

  const keys = createEmptyGraphKeys();
  request.transitions.forEach((transition) => {
    const transitionId = idMap.transitions[transition.clientId] ?? transition.id;
    if (transitionId <= 0) return;
    const [, gate, discriminator] = transition.clientId.split("|");
    keys.transitionIds[
      transitionKey(renameStage(transition.fromClientId), gate as WorkflowGateDto, discriminator)
    ] = transitionId;
  });
  request.stages.forEach((stage) =>
    stage.fields.forEach((field) =>
      field.options.forEach((option) => {
        const optionId = idMap.options[option.clientId] ?? option.id;
        if (optionId > 0) {
          keys.optionIds[optionKey(fieldRename.get(field.clientId) ?? field.clientId, option.name)] =
            optionId;
        }
      }),
    ),
  );
  request.variables.forEach((variable) => {
    const variableId = idMap.variables[variable.clientId] ?? variable.id;
    if (variableId > 0) keys.variableIds[variable.varKey] = variableId;
  });

  return { canvases: nextCanvases, keys, stageIdRenames: Object.fromEntries(stageRename) };
};
